/* eslint-disable sonarjs/cognitive-complexity */
import isEqual from 'lodash/isEqual';
import Auth from 'modules/Auth';
import { PermissionsEnum } from 'consts/permissions';
import { SOURCE_SYSTEM } from 'consts/sourceSystem';
import { DealStatus, DealType, FrontEndType } from 'components/common/types/Deal.types';
import { formatDateObjToIsoDateString, getCurrentDateWithTimezone } from 'utils/dateFormatUtil';
import { isReadOnly } from 'utils/isReadOnly';
import { isValidCPMValues } from 'utils/cpmValidationUtil';

export const isDraftFormValid = (formState) => {
  const {
    cpm: dealCpm,
    currentLine: { startDate, endDate, maxCPM, minCPM },
  } = formState.commonDeal;

  if (!startDate || !endDate) return false;

  if (!isValidCPMValues(dealCpm, minCPM, maxCPM)) {
    return false;
  }

  return true;
};

export const isFormValid = (formState, isReseller, isDV360AndPGAndDspSyncEnabled, hasVIOOHSelectFeatures) => {
  const {
    advertiser,
    brand,
    productCategory,
    cpm: dealCpm,
    dealType,
    sourceSystem,
    bookingStatusCode,
    currentLine: {
      environments,
      name,
      impressions,
      budget,
      cpm: lineCpm,
      frontEndType,
      mediaOwnerMeta,
      creativeType,
      maxCPM,
      minCPM,
    },
  } = formState.commonDeal;
  const { dsp, syncWithDsp, dspSeatId } = formState.programmatic;
  const isDV360Deal = sourceSystem === SOURCE_SYSTEM.DV360;
  const isDealTypeGuaranteed = dealType === DealType.GUARANTEED;

  if (isDealTypeGuaranteed && (!advertiser?.code || !brand?.code || !productCategory?.code)) return false;
  if (isDealTypeGuaranteed && !impressions && !budget) return false;
  if (!isDV360Deal && Number(dealCpm) <= 0 && Number(lineCpm) <= 0) return false;
  if (!dsp?.code) return false;
  if (syncWithDsp && !dspSeatId)
    return bookingStatusCode === DealStatus.LIVE || bookingStatusCode === DealStatus.APPROVED;
  if (isDV360AndPGAndDspSyncEnabled && !creativeType) return false;
  if (!environments?.length && !isReseller) return false;
  if ((!frontEndType || frontEndType !== FrontEndType.PLANNER) && !name) return false;
  if (isReseller) {
    if (!mediaOwnerMeta?.length) return false;
    if (mediaOwnerMeta.some((meta) => 'fee' in meta && meta.fee == null)) return false;
    if (hasVIOOHSelectFeatures && (!maxCPM || !isValidCPMValues(dealCpm, minCPM, maxCPM))) return false;
  }

  return true;
};

export const hasLineStarted = (line) => {
  if (!line?.startDate) return false;

  const currentDate = getCurrentDateWithTimezone(line?.earliestFrameTimezone);
  const formattedStartDate = formatDateObjToIsoDateString(line?.startDate, false);
  const formattedCurrentDate = formatDateObjToIsoDateString(currentDate, false);

  return formattedStartDate < formattedCurrentDate;
};

export const hasLineEnded = (line) => {
  if (!line?.endDate) return false;

  const currentDate = getCurrentDateWithTimezone(line?.earliestFrameTimezone);
  const formattedEndDate = formatDateObjToIsoDateString(line?.endDate, false);
  const formattedCurrentDate = formatDateObjToIsoDateString(currentDate, false);

  return formattedEndDate < formattedCurrentDate;
};

export const getActionConditionsValidator = ({
  formState,
  dealConfig,
  isReseller = false,
  isFetchingAvailability = false,
  isDV360AndPGAndDspSyncEnabled = false,
  hasVIOOHSelectFeatures = false,
  isProgrammaticDraftCampaignTransitionEnabled = false,
  isProgrammaticEndedCampaignTransitionEnabled = false,
}) => {
  const {
    isNewDeal,
    isEditingDisabled,
    isActionsDisabled,
    isCpmCampaignLevel,
    backupFormData: { lines },
    commonDeal: { bookingStatusCode, currentLine, dealType, sourceSystem },
    plannerSettings: { isSimplifiedPlanningMode },
  } = formState;

  const { maxAllowedLinesPerDeal } = dealConfig;

  const isLive = bookingStatusCode === DealStatus.LIVE;
  const isRejected = bookingStatusCode === DealStatus.REJECTED;
  const isEnded = bookingStatusCode === DealStatus.ENDED;
  const isTerminated = bookingStatusCode === DealStatus.TERMINATED;
  const isPending = bookingStatusCode === DealStatus.PENDING_APPROVAL;
  const isApproved = bookingStatusCode === DealStatus.APPROVED;
  const isPendingReservation = bookingStatusCode === DealStatus.PENDING_RESERVATION;
  const isCancelled = bookingStatusCode === DealStatus.CANCELLED;
  const isDraft = bookingStatusCode === DealStatus.DRAFT;
  const isNewLine = !currentLine?.id || !currentLine?.lineId;
  const activeLinesCount = lines.reduce((count, line) => (line.terminated ? count : count + 1), 0);
  const hasAvailabilityData = Boolean(currentLine?.availability.assets.length);
  const isDisabled =
    isEditingDisabled ||
    isFetchingAvailability ||
    isActionsDisabled ||
    !isFormValid(formState, isReseller, isDV360AndPGAndDspSyncEnabled, hasVIOOHSelectFeatures);
  const isDisabledExcludingFormValid = isEditingDisabled || isFetchingAvailability || isActionsDisabled;
  const lineLength = lines.filter((line) => line.terminated === false).length;
  const backupLine = lines.find(({ id }) => id === currentLine.id);
  const isDisabledSaveLine = isLive
    ? hasLineEnded(backupLine)
    : currentLine.startDate < new Date(new Date().setHours(0, 0, 0, 0));
  const hasLineChanged = isCpmCampaignLevel
    ? !isEqual(
        { ...currentLine, cpm: undefined, availability: undefined, preservedAvailability: undefined },
        { ...backupLine, cpm: undefined, availability: undefined, preservedAvailability: undefined },
      )
    : !isEqual(currentLine, backupLine);

  const readOnly = !isNewLine && isReadOnly(bookingStatusCode, isEditingDisabled, isTerminated);

  return {
    terminateLine: {
      show:
        Auth.hasPermission(PermissionsEnum.DEAL_REJECT) &&
        sourceSystem !== SOURCE_SYSTEM.DV360 &&
        hasLineStarted(backupLine) &&
        isLive,
      disabled: isDisabled || !hasAvailabilityData || isNewLine || currentLine.terminated || hasLineEnded(backupLine),
    },
    deleteLine: {
      show:
        sourceSystem !== SOURCE_SYSTEM.DV360 &&
        (isNewLine || !hasLineStarted(backupLine) || (hasLineStarted(backupLine) && !isLive)),
      disabled:
        isEditingDisabled ||
        isFetchingAvailability ||
        isActionsDisabled ||
        readOnly ||
        (!isNewLine && (!hasAvailabilityData || (activeLinesCount <= 1 && !isLive))),
    },
    clearForm: {
      show: isNewDeal,
      disabled: isEditingDisabled || isFetchingAvailability || readOnly,
    },
    resetForm: {
      show: !isNewDeal,
      disabled: isEditingDisabled || isFetchingAvailability || readOnly,
    },
    createLine: {
      show:
        Auth.hasPermission(PermissionsEnum.DEAL_CONFIRM) ||
        (Auth.hasPermission(PermissionsEnum.DEAL_PROPOSE) && !(isLive || isApproved)),
      disabled:
        isDisabled ||
        isNewLine ||
        lineLength >= maxAllowedLinesPerDeal ||
        isPendingReservation ||
        sourceSystem === SOURCE_SYSTEM.DV360 ||
        (readOnly && dealType === DealType.GUARANTEED) ||
        (dealType === DealType.GUARANTEED && isCancelled),
    },
    duplicateLine: {
      show: true,
      disabled:
        (isDraft ? !isDraftFormValid(formState) : isDisabled) ||
        isNewLine ||
        lineLength >= maxAllowedLinesPerDeal ||
        isPendingReservation ||
        sourceSystem === SOURCE_SYSTEM.DV360 ||
        (readOnly && dealType === DealType.GUARANTEED),
    },
    saveLine: {
      show:
        Auth.hasPermission(PermissionsEnum.DEAL_CONFIRM) ||
        (Auth.hasPermission(PermissionsEnum.DEAL_PROPOSE) && !(isLive || isApproved)),
      disabled:
        (isDraft ? !isDraftFormValid(formState) || !hasLineChanged : isDisabled) ||
        (!hasLineChanged && dealType === DealType.GUARANTEED) ||
        !hasAvailabilityData ||
        isNewDeal ||
        currentLine.terminated ||
        isDisabledSaveLine,
    },
    editDealInfo: {
      show: true,
      disabled: isEditingDisabled || isFetchingAvailability || readOnly,
    },
    saveDealInfo: {
      show:
        Auth.hasPermission(PermissionsEnum.DEAL_CONFIRM) ||
        (Auth.hasPermission(PermissionsEnum.DEAL_PROPOSE) && !(isLive || isApproved)),
      disabled:
        (isProgrammaticEndedCampaignTransitionEnabled ? isDisabledExcludingFormValid : isDisabled) ||
        (isNewDeal && !isDraft) ||
        !bookingStatusCode ||
        readOnly,
    },
    pendingApprovalDeal: {
      show: Auth.hasPermission(PermissionsEnum.DEAL_PROPOSE) && (isNewDeal || isDraft || isRejected),
      disabled:
        isDisabled ||
        !hasAvailabilityData ||
        readOnly ||
        (isDraft && lineLength >= 1 && !isProgrammaticDraftCampaignTransitionEnabled),
    },
    reservedDeal: {
      show: isPendingReservation,
      disabled: isDisabled || !hasAvailabilityData,
    },
    rejectDeal: {
      show: Auth.hasPermission(PermissionsEnum.DEAL_REJECT) && isPending,
      disabled: isDisabled || !hasAvailabilityData,
    },
    confirmDeal: {
      show: Auth.hasPermission(PermissionsEnum.DEAL_CONFIRM) && (isNewDeal || isDraft || isPending),
      disabled:
        isDisabled ||
        !hasAvailabilityData ||
        readOnly ||
        (isDraft && lineLength >= 1 && !isProgrammaticDraftCampaignTransitionEnabled),
    },
    cancelDeal: {
      show: Auth.hasPermission(PermissionsEnum.DEAL_REJECT) && isApproved,
      disabled: isDisabled || !hasAvailabilityData,
    },
    terminateDeal: {
      show: Auth.hasPermission(PermissionsEnum.DEAL_REJECT) && isLive,
      disabled: isDisabled || !hasAvailabilityData,
    },
    checkAllocate: {
      show: !isEnded && !isTerminated && dealType === DealType.GUARANTEED,
      disabled: isDisabled || readOnly,
    },
    createDraftDeal: {
      show:
        currentLine.frontEndType === FrontEndType.PLANNER &&
        Auth.hasPermission(PermissionsEnum.DEAL_PROPOSE) &&
        isSimplifiedPlanningMode &&
        isNewDeal &&
        dealType !== DealType.GUARANTEED &&
        hasAvailabilityData &&
        !isDraft,
      disabled: false,
    },
    editDraftDeal: {
      show: Auth.hasPermission(PermissionsEnum.DEAL_PROPOSE),
      disabled: (isDraft && !isDraftFormValid(formState)) || !hasAvailabilityData || readOnly,
    },
    updateEndedOrTerminatedDeal: {
      show: Auth.hasPermission(PermissionsEnum.DEAL_CONFIRM) && isProgrammaticEndedCampaignTransitionEnabled,
      disabled: isDisabledExcludingFormValid || !hasAvailabilityData || readOnly,
    },
  };
};
/* eslint-enable sonarjs/cognitive-complexity */
